import { h } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { createManufacturingConfiguration, createProductTypeRevision, deleteManufacturingConfiguration, deleteProductType, editProductTypeRevision, getManufacturingConfigurationGroups, getProductType, ManufacturingConfigurationGroup, ProductType, ProductTypeRevision } from "../../api/product-api";
import { route } from "../../App";
import { Snackbar, SnackbarShowCommand } from "../../components/Snackbar";
import { useCommand } from "../../hooks";
import { parseToDateAndFormatIfExists } from "../../utils";
import "./styles.scss";

function distinct<T>(array: T[]): T[] {
    return Array.from(new Set(array));
}

export function ProductTypePage(props: RoutableProps & { id?: string }) {
    const [productType, setProductType] = useState(null as null | ProductType);
    const [ordersSearch, setOrdersSearch] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(100);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();

    useEffect(() => {
        if (props.id) {
            getProductType(+props.id).then(async (label) => {
                setProductType(label);
            });
        }
        else {
            setProductType(null);
        }
    }, [page, count, refresh]);

    if (!productType) {
        return <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
            <div>Pobieranie danych...</div>
            <div class="loader"></div>
        </div>;
    }

    return <div class="product-list-page">
        <Snackbar doShow={showSnackbarCommand} />
        <div class="order-items paper flex flex-row" style="margin-top: 10px;">
            <div class="grow">
                <h1>Rodzaj produktu: {productType.name}</h1>
                <div>Brand: {productType.brand}</div>
                <div>Opis: {productType.description}</div>
                <div>GTIN-y: {distinct([productType.gtin, ...(productType.productTypeRevisions?.map(v => v.gtin) ?? [])]).filter(v => v).join(", ")}</div>
                <div>Rodzaj numeru seryjnego: {productType.serialNumberType?.name}</div>
                <div>Aktualna rewizja: {productType.currentProductTypeRevision?.name}</div>
                <div>Rejestrujący: {productType.creator?.email}</div>
                <div>Data rejestracji: {parseToDateAndFormatIfExists(productType.createdAt)}</div>
            </div>
            <div class="flex flex-row gap-2">
                <button type="button" class="primary" onClick={() => {
                    route("/product-type/edit/" + productType.id);
                }}>Edytuj</button>
                <div class={"self-start tooltip-left" + (productType.productTypeRevisions?.length ? " tooltip" : "")} data-tooltip={
                    productType.productTypeRevisions?.length ? "Usunięcie rodzaju produktu jest możliwe tylko wtedy, gdy nie ma żadnych rewizji tego rodzaju produktu." : ""}
                >
                    <button type="button" class={"error" + (productType.productTypeRevisions?.length ? ' disabled' : '')} onClick={() => {
                        deleteProductType(productType.id)
                            .then(() => route("/product-type-list"))
                            .catch(() => showSnackbarCommand.run("Nie udało się usunąć rodzaju produktu", "error", "error", 2000));
                    }}>Usuń</button>
                </div>
            </div>
        </div>

        <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Rewizje</h1>
            <div>
                <div style="margin-top: 10px;">
                    <table class="list selectable">
                        <thead>
                            <th>Nazwa</th>
                            <th>Nazwa (dla modelu)</th>
                            <th>Kolejność</th>
                            <th>Rejestrujący</th>
                            <th>Data rejestracji</th>
                        </thead>
                        <tbody>
                            {productType.productTypeRevisions!.map(v => <tr class="item" onClick={() => route("/product-type-revision/" + v.id)}>
                                <td>{v.name}</td>
                                <td>{v.modelName}</td>
                                <td>{v.version}</td>
                                <td>{v.creator.email}</td>
                                <td>{parseToDateAndFormatIfExists(v.createdAt)}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Dodaj nową rewizje</h1>
            <AddRevision productType={productType} onCommit={() => {
                setRefresh(refresh + 1);
            }} />
        </div>

        <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Zmiana aktualnej rewizji</h1>
            <ChangeRevision productType={productType} onCommit={async (revision) => {
                editProductTypeRevision(productType, revision)
                    .then(() => {
                        setRefresh(refresh + 1);
                        showSnackbarCommand.run("Pomyślnie zmieniono rewizję", "success", "check", 2000)
                    })
                    .catch(() => showSnackbarCommand.run("Nie udało się zmienić rewizji", "error", "error", 2000));
            }} />
        </div>

        <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Konfiguracje produkcyjne</h1>
            <div>
                <div style="margin-top: 10px;">
                    <table>
                        <thead>
                            <th>Nazwa</th>
                            <th>Podgląd</th>
                            <th>Grupa</th>
                            <th>Akcje</th>
                        </thead>
                        <tbody>
                            {productType.manufacturingConfigurationsProductTypes!.map(v => <tr>
                                <td>{v.name}</td>
                                <td>{v.preview}</td>
                                <td>{v.group?.name}</td>
                                <td class="text-center"><button type="button" class="error" onClick={() => {
                                    deleteManufacturingConfiguration(v.id);
                                    setRefresh(refresh + 1);
                                }}>Usuń</button></td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Dopisz do konfiguracji produkcyjnej</h1>
            <AddManufacturingConfiguration productType={productType} onCommit={() => {
                setRefresh(refresh + 1);
            }} />
        </div>
    </div>;
}

function ChangeRevision(props: { productType: ProductType, onCommit: (revision: ProductTypeRevision) => void }) {
    const [filteredRevisions, setFilteredRevisions] = useState([] as ProductTypeRevision[]);
    const [revisionsSearch, setRevisionsSearch] = useState("");
    const [selectedRevision, setSelectedRevision] = useState(null as ProductTypeRevision | null);

    useEffect(() => {
        if (props.productType.productTypeRevisions && revisionsSearch) {
            const filtered =
                props.productType.productTypeRevisions.filter(item => !revisionsSearch
                    || item.name.toLowerCase().indexOf(revisionsSearch.toLowerCase()) >= 0
                    || item.modelName.toLowerCase().indexOf(revisionsSearch.toLowerCase()) >= 0
                    || item.creator.email.toLowerCase().indexOf(revisionsSearch.toLowerCase()) >= 0
                    || parseToDateAndFormatIfExists(item.createdAt).toLocaleString().indexOf(revisionsSearch) >= 0)
                    .slice(0, 10);

            if (selectedRevision && filtered.indexOf(selectedRevision) < 0) {
                filtered.unshift(selectedRevision);
            }

            setFilteredRevisions(filtered);
        }
        else {
            setFilteredRevisions(selectedRevision ? [selectedRevision] : []);
        }
    }, [props.productType, revisionsSearch, selectedRevision])

    return <div>
        <div>
            <div class="search">
                <input class="search-text" placeholder="Wyszukaj..." value={revisionsSearch} onInput={(e) => {
                    setRevisionsSearch(e.currentTarget.value);
                }} />
            </div>
            <div style="margin-top: 10px;">
                <table class="list selectable">
                    <thead>
                        <th>Nazwa</th>
                        <th>Rewizja</th>
                        <th>Rejestrujący</th>
                        <th>Data rejestracji</th>
                    </thead>
                    <tbody>
                        {filteredRevisions!.map(v => <tr class={"item" + (v === selectedRevision ? " selected" : "")} onClick={() => {
                            setSelectedRevision(selectedRevision === v ? null : v);
                            setRevisionsSearch("");
                        }}>
                            <td>{v.name}</td>
                            <td>{v.modelName}</td>
                            <td>{v.creator.email}</td>
                            <td>{parseToDateAndFormatIfExists(v.createdAt)}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
            <div style="margin-top: 10px;">
                <button type="button" class={selectedRevision ? "" : "disabled"} onClick={async () => {
                    if (selectedRevision) {
                        // await addLabelToProductTypeRevision(selectedLabel, props.productType);
                        props.onCommit(selectedRevision);
                    }
                }}>Zmień rewizję</button>
            </div>
        </div>
    </div>;
}

function AddRevision(props: { productType: ProductType, onCommit: () => void }) {
    const [name, setName] = useState("");
    const [modelName, setModelName] = useState("");
    const [gtin, setGtin] = useState("");
    const [changelog, setChangelog] = useState("");
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();

    return <div>
        <Snackbar doShow={showSnackbarCommand} />
        <form>
            <div class="flex-col">
                <div className="grid grid-cols-[auto_auto] gap-4 justify-start">
                    <label htmlFor="name" class="justify-self-end">Nazwa:</label>
                    <input
                        required
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        type="text"
                        className="px-2 py-1"
                    />
                    <label htmlFor="modelName" class="justify-self-end">Nazwa (dla modelu):</label>
                    <input
                        required
                        id="modelName"
                        value={modelName}
                        onChange={(e) => setModelName(e.currentTarget.value)}
                        type="text"
                        className="px-2 py-1"
                        size={40}
                    />
                    <label htmlFor="gtin" class="justify-self-end">GTIN:</label>
                    <input
                        required
                        id="gtin"
                        value={gtin}
                        onChange={(e) => setGtin(e.currentTarget.value)}
                        type="text"
                        className="px-2 py-1"
                        size={40}
                    />
                    <label htmlFor="changelog" class="justify-self-end">Lista zmian:</label>
                    <textarea
                        id="changelog"
                        value={changelog}
                        onChange={(e) => setChangelog(e.currentTarget.value)}
                        className="px-2 py-1 w-full min-h-16"
                    />
                </div>
                <div style="margin-top: 10px;">
                    <button type="submit" class={name && modelName && gtin ? "" : "disabled"} onClick={async (e) => {
                        e.preventDefault();

                        if (name && modelName) {
                            try {
                                await createProductTypeRevision(props.productType, name, modelName, gtin, changelog);
                                props.onCommit();
                            }
                            catch (ex: unknown) {
                                showSnackbarCommand.run("Nie udało się utworzyć rewizji: " + (ex instanceof Error ? ex.message : (ex as any).toString()), "error", "error", 2000);
                            }
                        }
                    }}>Dodaj rewizję</button>
                </div>
            </div>
        </form>
    </div>;
}

function AddManufacturingConfiguration(props: { productType: ProductType, onCommit: () => void }) {
    const [name, setName] = useState("LunaEMG");
    const [preview, setPreview] = useState("");
    const [group, setGroup] = useState(0);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const [availableGroups, setAvailableGroups] = useState([] as ManufacturingConfigurationGroup[]);
    const availableImages = JSON.parse(process.env.IMAGES_LIST ?? "[]") as string[];

    useEffect(() => {
        getManufacturingConfigurationGroups().then(groups => {
            setAvailableGroups(groups);
        }).catch(() => {
            showSnackbarCommand.run("Nie udało się pobrać grup konfiguracji produkcyjnych", "error", "error", 2000);
        });
    }, []);

    return <div>
        <Snackbar doShow={showSnackbarCommand} />
        <form>
            <div class="flex-col">
                <div class="flex flex-row items-start">
                    <div className="grid grid-cols-[auto_auto] gap-4 justify-start items-start">
                        <label htmlFor="name" class="justify-self-end">Konfiguracja:</label>
                        <select
                            required
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            type="text"
                            className="px-2 py-1"
                        >
                            <option value="LunaEMG">LunaEMG</option>
                            <option value="MezosSIT">MezosSIT</option>
                            <option value="StellaBIO">StellaBIO</option>
                            <option value="SidraLEG">SidraLEG</option>
                            <option value="MeissaOT">MeissaOT</option>
                        </select>
                        <label htmlFor="preview" class="justify-self-end">Podgląd:</label>
                        {availableImages.length === 0 ? <input
                            required
                            id="preview"
                            value={preview}
                            onChange={(e) => setPreview(e.currentTarget.value)}
                            type="text"
                            className="px-2 py-1"
                            size={40}
                        /> : <select
                            required
                            id="preview"
                            value={preview}
                            onChange={(e) => setPreview(e.currentTarget.value)}
                            type="text"
                            className="px-2 py-1">
                            <option value=""></option>
                            {availableImages.map(v => <option value={v} key={v}>{v}</option>)}
                        </select>}
                        <label htmlFor="group" class="justify-self-end">Grupa:</label>
                        <select
                            required
                            id="group"
                            value={group}
                            onChange={(e) => setGroup(+e.currentTarget.value)}
                            type="text"
                            className="px-2 py-1"
                        >
                            {availableGroups.map(v => <option value={v.id} key={v.id}>{v.name}</option>)}
                        </select>
                    </div>
                    {preview && <img src={"/images/" + preview} alt="Podgląd" class="ml-2 max-w-48 max-h-48" />}
                </div>
                <div style="margin-top: 10px;">
                    <button type="submit" class={name ? "" : "disabled"} onClick={async (e) => {
                        e.preventDefault();

                        if (name) {
                            try {
                                await createManufacturingConfiguration(props.productType, name, "images/" + preview, group);
                                props.onCommit();
                            }
                            catch (ex: unknown) {
                                showSnackbarCommand.run("Nie udało się dodać do konfiguracji: " + (ex instanceof Error ? ex.message : (ex as any).toString()), "error", "error", 2000);
                            }
                        }
                    }}>Dodaj do konfiguracji</button>
                </div>
            </div>
        </form>
    </div>;
}
